import * as React from "react"

import {
  AFAXYS_COUPON_TERMS_HREF,
  AFAXYS_HEALTH_DATA_POLICY_HREF,
  AFAXYS_NOTICE_OF_FINANCIAL_INCENTIVE_HREF,
  AFAXYS_SIGNUP_PRIVACY_POLICY_HREF,
  AFAXYS_SIGNUP_TERMS_OF_USE_HREF,
} from "../../data/legal"
import {
  RECAPTCHA_ENTERPRISE_SUBMIT_ACTION,
  useRecaptchaEnterprise,
} from "../../utils/useRecaptchaEnterprise"
import { Button } from "../Button"
import { ModalShell } from "../ModalShell"

const ECONTRA_PACKAGING = "/figma/home/econtra-packaging.png"
const ECONTRA_SIGNUP_ENDPOINT =
  "https://l4me1s60te.execute-api.us-east-1.amazonaws.com/prod/submit"
const SUBMIT_ERROR_MESSAGE =
  "We couldn't verify your submission. Please try again."

const modalMonoStyle: React.CSSProperties = {
  fontFamily: '"DM Mono", var(--font-mono)',
}

export type EcontraSignupModalMode = "form" | "success"

export type EcontraSignupModalProps = {
  isOpen: boolean
  mode: EcontraSignupModalMode
  onClose: () => void
  onSubmit: (data: { email: string; phone: string }) => void
}

type SignupErrors = {
  email: string
  emailConsent: string
  phone: string
}

function joinClasses(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ")
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function validateSignup(
  email: string,
  phoneNumber: string,
  emailConsent: boolean,
): SignupErrors {
  const normalizedEmail = email.trim()
  const normalizedPhone = phoneNumber.trim()

  return {
    email: !normalizedEmail
      ? "Email is required."
      : !isValidEmail(normalizedEmail)
        ? "Enter a valid email address."
        : "",
    emailConsent: !emailConsent ? "You must agree to the terms to continue." : "",
    phone:
      normalizedPhone && normalizedPhone.length < 10
        ? "Enter a 10-digit phone number."
        : "",
  }
}

function hasSignupErrors(errors: SignupErrors) {
  return Boolean(errors.email || errors.emailConsent || errors.phone)
}

function formatPhone(digits: string) {
  const slice = digits.slice(0, 10)
  const part1 = slice.slice(0, 3)
  const part2 = slice.slice(3, 6)
  const part3 = slice.slice(6, 10)

  if (!part1) {
    return ""
  }

  if (part1.length < 3) {
    return `(${part1}`
  }

  if (!part2) {
    return `(${part1}) `
  }

  if (part2.length < 3) {
    return `(${part1}) ${part2}`
  }

  if (!part3) {
    return `(${part1}) ${part2}-`
  }

  return `(${part1}) ${part2}-${part3}`
}

function getDigitIndexFromCursor(value: string, cursor: number) {
  return value.slice(0, cursor).replace(/[^\d]/g, "").length
}

function getCursorFromDigitIndex(value: string, digitIndex: number) {
  if (digitIndex <= 0) {
    return 0
  }

  let count = 0

  for (let index = 0; index < value.length; index += 1) {
    if (/\d/.test(value[index])) {
      count += 1

      if (count === digitIndex) {
        return index + 1
      }
    }
  }

  return value.length
}

function EcontraPack({
  mobile,
}: {
  mobile: boolean
}) {
  return (
    <div
      className={joinClasses(
        "relative flex items-center justify-center",
        mobile ? "h-[117px] w-[91px]" : "h-[207px] w-[160px]",
      )}
      style={{ transform: "rotate(-6.28deg)" }}
    >
      <div
        aria-hidden="true"
        className={joinClasses(
          "absolute rounded-[10.898px] bg-accent-500",
          mobile
            ? "h-[109px] w-[80px]"
            : "h-[193px] w-[140px]",
        )}
      />
      <img
        alt=""
        aria-hidden="true"
        className={joinClasses(
          "relative z-[1] object-contain",
          mobile
            ? "h-[97px] w-[69px]"
            : "h-[175px] w-[124px]",
        )}
        src={ECONTRA_PACKAGING}
      />
    </div>
  )
}

function ValidationPopover({
  id,
  message,
}: {
  id: string
  message: string
}) {
  return (
    <div
      className="absolute left-1/2 top-full z-10 mt-2 flex w-max max-w-[90%] -translate-x-1/2 items-start gap-2 rounded-md border border-black bg-white px-3 py-2 text-xs text-black shadow-[2px_2px_0_0_#000]"
      id={id}
      role="alert"
    >
      <span className="mt-[1px] inline-flex h-4 w-4 items-center justify-center rounded-full border border-black text-[10px] font-bold leading-none">
        !
      </span>
      <span>{message}</span>
    </div>
  )
}

function SignupField({
  autoComplete,
  dashed = false,
  error,
  errorId,
  id,
  inputMode,
  inputRef,
  label,
  mobile,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  type,
  value,
}: {
  autoComplete: string
  dashed?: boolean
  error?: string
  errorId: string
  id: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"]
  inputRef?: { current: HTMLInputElement | null }
  label: string
  mobile: boolean
  onChange: React.ChangeEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  placeholder: string
  type: React.HTMLInputTypeAttribute
  value: string
}) {
  return (
    <div className="relative">
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <div
        className={joinClasses(
          "flex items-center rounded-[100px] border-2 border-ink-950 px-[24px]",
          dashed ? "border-dashed bg-white" : "bg-[#E1F4FE]",
          mobile ? "h-[49px] w-[290px]" : "h-[56px] w-[329px]",
        )}
      >
        <input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? "true" : "false"}
          autoComplete={autoComplete}
          className={joinClasses(
            "w-full border-0 bg-transparent p-0 text-ink-950 outline-none placeholder:text-ink-950 text-[16px] leading-none",
          )}
          id={id}
          inputMode={inputMode}
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          style={modalMonoStyle}
          type={type}
          value={value}
        />
      </div>
      {error ? <ValidationPopover id={errorId} message={error} /> : null}
    </div>
  )
}

function ConsentCheckbox({
  checked,
  children,
  error,
  errorId,
  id,
  mobile,
  onChange,
}: {
  checked: boolean
  children: React.ReactNode
  error?: string
  errorId: string
  id: string
  mobile: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="relative">
      <div
        className={joinClasses(
          "flex items-start gap-3",
          mobile ? "w-[290px]" : "w-[329px]",
        )}
      >
        <input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? "true" : "false"}
          checked={checked}
          className="mt-[3px] h-4 w-4 shrink-0 cursor-pointer accent-ink-950"
          id={id}
          onChange={(e) => onChange(e.target.checked)}
          type="checkbox"
        />
        <label
          className={joinClasses(
            "cursor-pointer font-sans text-ink-950",
            mobile ? "text-[12px] leading-[1.3]" : "text-[13px] leading-[1.3]",
          )}
          htmlFor={id}
        >
          {children}
        </label>
      </div>
      {error ? <ValidationPopover id={errorId} message={error} /> : null}
    </div>
  )
}

function EmailConsentLabel() {
  return (
    <>
      I agree to the{" "}
      <a
        className="underline"
        href={AFAXYS_NOTICE_OF_FINANCIAL_INCENTIVE_HREF}
        rel="noreferrer"
        target="_blank"
      >
        Notice of Financial Incentive
      </a>
      ,{" "}
      <a
        className="underline"
        href={AFAXYS_COUPON_TERMS_HREF}
        rel="noreferrer"
        target="_blank"
      >
        Coupon Terms
      </a>
      ,{" "}
      <a
        className="underline"
        href={AFAXYS_SIGNUP_PRIVACY_POLICY_HREF}
        rel="noreferrer"
        target="_blank"
      >
        Privacy Policy
      </a>
      ,{" "}
      <a
        className="underline"
        href={AFAXYS_HEALTH_DATA_POLICY_HREF}
        rel="noreferrer"
        target="_blank"
      >
        Health Data Policy
      </a>
      ,{" "}
      <a
        className="underline"
        href={AFAXYS_SIGNUP_TERMS_OF_USE_HREF}
        rel="noreferrer"
        target="_blank"
      >
        Terms of Use
      </a>
      .
    </>
  )
}

const PHONE_CONSENT_TEXT =
  "I want to receive automated promotional texts from Afaxys at the number provided above. I acknowledge that accepting this consent is not required for the purchase of any goods or services and that Afaxys may send communications through vendors. Msg and data rates may apply."

function FormDesktop({
  email,
  emailConsent,
  emailConsentError,
  emailConsentErrorId,
  emailConsentId,
  emailError,
  emailErrorId,
  emailInputId,
  emailInputRef,
  emailIsPressed,
  isSubmitting,
  onCellNumberChange,
  onCellNumberFocus,
  onCellNumberKeyDown,
  onEmailChange,
  onEmailConsentChange,
  onEmailFocus,
  onPhoneConsentChange,
  onSubmit,
  phoneConsent,
  phoneConsentId,
  phoneError,
  phoneErrorId,
  phoneInputId,
  phoneInputRef,
  phoneIsPressed,
  phoneNumber,
  showCellField,
  submitError,
}: {
  email: string
  emailConsent: boolean
  emailConsentError: string
  emailConsentErrorId: string
  emailConsentId: string
  emailError: string
  emailErrorId: string
  emailInputId: string
  emailInputRef: React.RefObject<HTMLInputElement | null>
  emailIsPressed: boolean
  isSubmitting: boolean
  onCellNumberChange: React.ChangeEventHandler<HTMLInputElement>
  onCellNumberFocus: React.FocusEventHandler<HTMLInputElement>
  onCellNumberKeyDown: React.KeyboardEventHandler<HTMLInputElement>
  onEmailChange: React.ChangeEventHandler<HTMLInputElement>
  onEmailConsentChange: (checked: boolean) => void
  onEmailFocus: React.FocusEventHandler<HTMLInputElement>
  onPhoneConsentChange: (checked: boolean) => void
  onSubmit: React.FormEventHandler<HTMLFormElement>
  phoneConsent: boolean
  phoneConsentId: string
  phoneError: string
  phoneErrorId: string
  phoneInputId: string
  phoneInputRef: React.RefObject<HTMLInputElement | null>
  phoneIsPressed: boolean
  phoneNumber: string
  showCellField: boolean
  submitError: string
}) {
  return (
    <div
      className="hidden w-[632px] px-[48px] pb-[40px] pt-[20px] lg:block"
      data-node-id="4290:6594"
    >
      <div className="flex flex-col items-center gap-[30px]">
        <div className="relative h-[207px] w-[598px] shrink-0">
          <div className="absolute left-0 top-[20px]">
            <EcontraPack mobile={false} />
          </div>

          <div className="absolute left-[200px] top-0 flex h-[207px] w-[330px] flex-col items-start justify-center text-ink-950">
            <h2 className="font-display text-[39.67px] font-bold leading-[57.303px] text-center">
              Sign Up. Save Big.
              <br />
              Stay Ready.
            </h2>
            <p className="mt-4 font-sans text-[18px] font-bold leading-[1.25] tracking-[-0.198px] text-center">
              Sign up for 15% off EContra, our emergency contraceptive.
            </p>
          </div>
        </div>

        <p className="w-[536px] text-center font-sans text-[16px] font-normal leading-[1.25] text-ink-950 mt-4">
          Sign up below to receive promotional emails and texts about Afaxys
          products and services from Afaxys and its service providers.
        </p>

        <form className="flex flex-col items-center" noValidate onSubmit={onSubmit}>
          <div className="flex flex-col items-center gap-[25px]">
            <SignupField
              autoComplete="email"
              dashed={!emailIsPressed}
              error={emailError}
              errorId={emailErrorId}
              id={emailInputId}
              inputRef={emailInputRef}
              label="Email address"
              mobile={false}
              onChange={onEmailChange}
              onFocus={onEmailFocus}
              placeholder="ENTER YOUR EMAIL ADDRESS"
              type="email"
              value={email}
            />
            <ConsentCheckbox
              checked={emailConsent}
              error={emailConsentError}
              errorId={emailConsentErrorId}
              id={emailConsentId}
              mobile={false}
              onChange={onEmailConsentChange}
            >
              <EmailConsentLabel />
            </ConsentCheckbox>
            {showCellField ? (
              <>
                <SignupField
                  autoComplete="tel"
                  dashed={!phoneIsPressed}
                  error={phoneError}
                  errorId={phoneErrorId}
                  id={phoneInputId}
                  inputMode="numeric"
                  inputRef={phoneInputRef}
                  label="Cell phone number (optional)"
                  mobile={false}
                  onChange={onCellNumberChange}
                  onFocus={onCellNumberFocus}
                  onKeyDown={onCellNumberKeyDown}
                  placeholder="CELL PHONE NUMBER (OPTIONAL)"
                  type="tel"
                  value={formatPhone(phoneNumber)}
                />
                <ConsentCheckbox
                  checked={phoneConsent}
                  errorId={`${phoneConsentId}-error`}
                  id={phoneConsentId}
                  mobile={false}
                  onChange={onPhoneConsentChange}
                >
                  {PHONE_CONSENT_TEXT}
                </ConsentCheckbox>
              </>
            ) : null}
            {submitError ? (
              <p className="max-w-[360px] text-center text-sm font-medium text-ink-950" role="alert">
                {submitError}
              </p>
            ) : null}
            <Button
              className="!w-[200px] !bg-accent-500 disabled:!cursor-not-allowed"
              disabled={isSubmitting}
              icon={isSubmitting ? "none" : "horizontal"}
              stateOverride="hover"
              type="submit"
              variant="pill"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function FormMobile({
  email,
  emailConsent,
  emailConsentError,
  emailConsentErrorId,
  emailConsentId,
  emailError,
  emailErrorId,
  emailInputId,
  emailInputRef,
  emailIsPressed,
  isSubmitting,
  onCellNumberChange,
  onCellNumberFocus,
  onCellNumberKeyDown,
  onEmailChange,
  onEmailConsentChange,
  onEmailFocus,
  onPhoneConsentChange,
  onSubmit,
  phoneConsent,
  phoneConsentId,
  phoneError,
  phoneErrorId,
  phoneInputId,
  phoneInputRef,
  phoneIsPressed,
  phoneNumber,
  showCellField,
  submitError,
}: {
  email: string
  emailConsent: boolean
  emailConsentError: string
  emailConsentErrorId: string
  emailConsentId: string
  emailError: string
  emailErrorId: string
  emailInputId: string
  emailInputRef: React.RefObject<HTMLInputElement | null>
  emailIsPressed: boolean
  isSubmitting: boolean
  onCellNumberChange: React.ChangeEventHandler<HTMLInputElement>
  onCellNumberFocus: React.FocusEventHandler<HTMLInputElement>
  onCellNumberKeyDown: React.KeyboardEventHandler<HTMLInputElement>
  onEmailChange: React.ChangeEventHandler<HTMLInputElement>
  onEmailConsentChange: (checked: boolean) => void
  onEmailFocus: React.FocusEventHandler<HTMLInputElement>
  onPhoneConsentChange: (checked: boolean) => void
  onSubmit: React.FormEventHandler<HTMLFormElement>
  phoneConsent: boolean
  phoneConsentId: string
  phoneError: string
  phoneErrorId: string
  phoneInputId: string
  phoneInputRef: React.RefObject<HTMLInputElement | null>
  phoneIsPressed: boolean
  phoneNumber: string
  showCellField: boolean
  submitError: string
}) {
  return (
    <div className="px-[20px] pb-[40px] pt-[10px] lg:hidden" data-node-id="4290:6638">
      <div className="flex justify-end">
        <div aria-hidden="true" className="h-[25px] w-[25px]" />
      </div>

      <div className="mt-2 flex items-center justify-center gap-[14px]">
        <div className="shrink-0">
          <EcontraPack mobile />
        </div>

        <div className="flex w-[219px] flex-col items-center justify-end text-center text-ink-950">
          <h2 className="w-[220px] font-display text-[25px] font-bold leading-[35px]">
            Sign Up. Save Big.
            <br />
            Stay Ready.
          </h2>
          <p className="mt-1 font-sans text-[14px] font-bold leading-[1.2] text-ink-950">
            Sign up for 15% off EContra, our emergency contraceptive.
          </p>
        </div>
      </div>

      <p className="mt-4 text-center font-sans text-[16px] font-normal leading-[20px] text-ink-950">
        Sign up below to receive promotional emails and texts about Afaxys
        products and services from Afaxys and its service providers.
      </p>

      <form
        className="mt-[30px] flex flex-col items-center"
        noValidate
        onSubmit={onSubmit}
      >
        <div className="flex flex-col items-center gap-[25px]">
          <SignupField
            autoComplete="email"
            dashed={!emailIsPressed}
            error={emailError}
            errorId={emailErrorId}
            id={emailInputId}
            inputRef={emailInputRef}
            label="Email address"
            mobile
            onChange={onEmailChange}
            onFocus={onEmailFocus}
            placeholder="ENTER YOUR EMAIL ADDRESS"
            type="email"
            value={email}
          />
          <ConsentCheckbox
            checked={emailConsent}
            error={emailConsentError}
            errorId={emailConsentErrorId}
            id={emailConsentId}
            mobile
            onChange={onEmailConsentChange}
          >
            <EmailConsentLabel />
          </ConsentCheckbox>
          {showCellField ? (
            <>
              <SignupField
                autoComplete="tel"
                dashed={!phoneIsPressed}
                error={phoneError}
                errorId={phoneErrorId}
                id={phoneInputId}
                inputMode="numeric"
                inputRef={phoneInputRef}
                label="Cell phone number (optional)"
                mobile
                onChange={onCellNumberChange}
                onFocus={onCellNumberFocus}
                onKeyDown={onCellNumberKeyDown}
                placeholder="CELL PHONE NUMBER (OPTIONAL)"
                type="tel"
                value={formatPhone(phoneNumber)}
              />
              <ConsentCheckbox
                checked={phoneConsent}
                errorId={`${phoneConsentId}-error`}
                id={phoneConsentId}
                mobile
                onChange={onPhoneConsentChange}
              >
                {PHONE_CONSENT_TEXT}
              </ConsentCheckbox>
            </>
          ) : null}
          {submitError ? (
            <p className="max-w-[280px] text-center text-sm font-medium text-ink-950" role="alert">
              {submitError}
            </p>
          ) : null}
          <Button
            className="!w-[171px] !bg-accent-500 disabled:!cursor-not-allowed"
            disabled={isSubmitting}
            icon={isSubmitting ? "none" : "horizontal"}
            stateOverride="hover"
            type="submit"
            variant="pill"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </form>
    </div>
  )
}

function SuccessDesktop({
  onClose,
}: {
  onClose: () => void
}) {
  return (
    <div className="hidden px-[48px] pb-[40px] pt-[20px] lg:block" data-node-id="4290:6620">
      <div className="flex justify-end">
        <div aria-hidden="true" className="h-[35px] w-[35px]" />
      </div>

      <div className="relative ml-[9px] mt-[-1px] h-[207px] w-[598px]">
        <div className="absolute left-0 top-0">
          <EcontraPack mobile={false} />
        </div>

        <div className="absolute left-[132px] top-[41px] flex h-[120px] w-[466px] flex-col items-center justify-center text-center text-ink-950">
          <h2 className="w-[337px] font-display text-[39.67px] font-bold leading-[57.303px]">
            Thanks for
            <br />
            Signing Up
          </h2>
          <p className="mt-[14.876px] w-[386px] font-sans text-[18px] font-normal leading-[1.25] tracking-[-0.198px]">
            Your 15% off EContra is on its way to your inbox.
          </p>
        </div>
      </div>

      <div className="mt-[40px] flex justify-center">
        <Button
          className="!w-[204px] !cursor-pointer !bg-accent-500"
          onClick={onClose}
          stateOverride="hover"
          type="button"
          variant="pill"
        >
          Close
        </Button>
      </div>
    </div>
  )
}

function SuccessMobile({
  onClose,
}: {
  onClose: () => void
}) {
  return (
    <div className="px-[20px] pb-[40px] pt-[10px] lg:hidden" data-node-id="4290:6663">
      <div className="flex justify-end">
        <div aria-hidden="true" className="h-[25px] w-[25px]" />
      </div>

      <div className="mt-2 flex items-start justify-center gap-[16px]">
        <div className="shrink-0">
          <EcontraPack mobile />
        </div>

        <div className="flex w-[178px] flex-col items-center text-center text-ink-950">
          <h2 className="w-full font-display text-[25px] font-bold leading-[35px]">
            Thanks for
            <br />
            Signing Up
          </h2>
        </div>
      </div>

      <p className="mt-5 text-center font-sans text-[16px] font-normal leading-[20px] text-ink-950">
        Your 15% off EContra is on its way to your inbox.
      </p>

      <div className="mt-5 flex justify-center">
        <Button
          className="!w-[170px] !cursor-pointer !bg-accent-500"
          onClick={onClose}
          stateOverride="hover"
          type="button"
          variant="pill"
        >
          Close
        </Button>
      </div>
    </div>
  )
}

export const EcontraSignupModal: React.FC<EcontraSignupModalProps> = ({
  isOpen,
  mode,
  onClose,
  onSubmit,
}) => {
  const emailInputRef = React.useRef<HTMLInputElement | null>(null)
  const phoneInputRef = React.useRef<HTMLInputElement | null>(null)
  const [email, setEmail] = React.useState("")
  const [phoneNumber, setPhoneNumber] = React.useState("")
  const [emailConsent, setEmailConsent] = React.useState(false)
  const [phoneConsent, setPhoneConsent] = React.useState(false)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState("")
  const [emailIsPressed, setEmailIsPressed] = React.useState(false)
  const [phoneIsPressed, setPhoneIsPressed] = React.useState(false)
  const [errors, setErrors] = React.useState<SignupErrors>({
    email: "",
    emailConsent: "",
    phone: "",
  })
  const titleId = React.useId()
  const descriptionId = React.useId()
  const emailInputId = React.useId()
  const phoneInputId = React.useId()
  const emailConsentId = React.useId()
  const phoneConsentId = React.useId()
  const emailErrorId = `${emailInputId}-error`
  const phoneErrorId = `${phoneInputId}-error`
  const emailConsentErrorId = `${emailConsentId}-error`
  const { execute: executeRecaptcha } = useRecaptchaEnterprise(isOpen)

  React.useEffect(() => {
    if (isOpen) {
      return
    }

    setEmail("")
    setPhoneNumber("")
    setEmailConsent(false)
    setPhoneConsent(false)
    setIsExpanded(false)
    setIsSubmitting(false)
    setSubmitError("")
    setEmailIsPressed(false)
    setPhoneIsPressed(false)
    setErrors({ email: "", emailConsent: "", phone: "" })
  }, [isOpen])

  function handleEmailFocus() {
    setIsExpanded(true)
    setEmailIsPressed(true)
  }

  function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value)
    setSubmitError("")

    if (errors.email) {
      setErrors(currentErrors => ({ ...currentErrors, email: "" }))
    }
  }

  function handleEmailConsentChange(checked: boolean) {
    setEmailConsent(checked)
    setSubmitError("")

    if (errors.emailConsent) {
      setErrors(currentErrors => ({ ...currentErrors, emailConsent: "" }))
    }
  }

  function handlePhoneFocus() {
    setPhoneIsPressed(true)
  }

  function handlePhoneChange(event: React.ChangeEvent<HTMLInputElement>) {
    setPhoneNumber(event.target.value.replace(/[^\d]/g, "").slice(0, 10))
    setSubmitError("")

    if (errors.phone) {
      setErrors(currentErrors => ({ ...currentErrors, phone: "" }))
    }
  }

  function handlePhoneKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Backspace" && event.key !== "Delete") {
      return
    }

    const target = event.currentTarget
    const selectionStart = target.selectionStart ?? target.value.length
    const selectionEnd = target.selectionEnd ?? target.value.length
    const startDigit = getDigitIndexFromCursor(target.value, selectionStart)
    const endDigit = getDigitIndexFromCursor(target.value, selectionEnd)

    event.preventDefault()
    setSubmitError("")
    setPhoneNumber(currentPhoneNumber => {
      let nextPhoneNumber = currentPhoneNumber
      let nextDigitIndex = startDigit

      if (selectionStart !== selectionEnd) {
        nextPhoneNumber =
          currentPhoneNumber.slice(0, startDigit) +
          currentPhoneNumber.slice(endDigit)
        nextDigitIndex = startDigit
      } else if (event.key === "Backspace" && startDigit > 0) {
        nextPhoneNumber =
          currentPhoneNumber.slice(0, startDigit - 1) +
          currentPhoneNumber.slice(startDigit)
        nextDigitIndex = startDigit - 1
      } else if (event.key === "Delete" && startDigit < currentPhoneNumber.length) {
        nextPhoneNumber =
          currentPhoneNumber.slice(0, startDigit) +
          currentPhoneNumber.slice(startDigit + 1)
        nextDigitIndex = startDigit
      }

      window.requestAnimationFrame(() => {
        const input = phoneInputRef.current

        if (!input) {
          return
        }

        const formatted = formatPhone(nextPhoneNumber)
        const cursor = getCursorFromDigitIndex(formatted, nextDigitIndex)
        input.setSelectionRange(cursor, cursor)
      })

      return nextPhoneNumber
    })

    if (errors.phone) {
      setErrors(currentErrors => ({ ...currentErrors, phone: "" }))
    }
  }

  function handlePhoneConsentChange(checked: boolean) {
    setPhoneConsent(checked)
    setSubmitError("")
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const validationErrors = validateSignup(email, phoneNumber, emailConsent)
    setErrors(validationErrors)
    setIsExpanded(true)
    setEmailIsPressed(true)

    if (hasSignupErrors(validationErrors) || isSubmitting) {
      return
    }

    const trimmedEmail = email.trim()
    const capturedPhone = phoneConsent ? phoneNumber : ""
    setEmail(trimmedEmail)
    setSubmitError("")
    setIsSubmitting(true)

    try {
      const token = await executeRecaptcha(RECAPTCHA_ENTERPRISE_SUBMIT_ACTION)
      const response = await fetch(ECONTRA_SIGNUP_ENDPOINT, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          email: trimmedEmail,
          phone: capturedPhone,
          terms: true,
          rcToken: token,
        }),
      })

      if (!response.ok) {
        throw new Error(`EContra signup failed with status ${response.status}.`)
      }

      onSubmit({ email: trimmedEmail, phone: capturedPhone })
    } catch {
      setSubmitError(SUBMIT_ERROR_MESSAGE)
    } finally {
      setIsSubmitting(false)
    }
  }

  const title =
    mode === "success"
      ? "Thanks for Signing Up"
      : "Sign Up. Save Big. Stay Ready."
  const description =
    mode === "success"
      ? "Your 15% off EContra is on its way to your inbox."
      : "Sign up below to receive promotional emails and texts about Afaxys products and services from Afaxys and its service providers."

  return (
    <ModalShell
      ariaDescribedby={descriptionId}
      ariaLabelledby={titleId}
      className="w-[340px] rounded-[15px] border-[12px] border-accent-500 bg-white lg:w-[672px] lg:border-[20px]"
      isOpen={isOpen}
      onClose={onClose}
    >
      <h2 className="sr-only" id={titleId}>
        {title}
      </h2>
      <p className="sr-only" id={descriptionId}>
        {description}
      </p>

      {mode === "success" ? (
        <>
          <SuccessDesktop onClose={onClose} />
          <SuccessMobile onClose={onClose} />
        </>
      ) : (
        <>
          <FormDesktop
            email={email}
            emailConsent={emailConsent}
            emailConsentError={errors.emailConsent}
            emailConsentErrorId={`${emailConsentErrorId}-desktop`}
            emailConsentId={`${emailConsentId}-desktop`}
            emailError={errors.email}
            emailErrorId={`${emailErrorId}-desktop`}
            emailInputId={`${emailInputId}-desktop`}
            emailInputRef={emailInputRef}
            emailIsPressed={emailIsPressed}
            isSubmitting={isSubmitting}
            onCellNumberChange={handlePhoneChange}
            onCellNumberFocus={handlePhoneFocus}
            onCellNumberKeyDown={handlePhoneKeyDown}
            onEmailChange={handleEmailChange}
            onEmailConsentChange={handleEmailConsentChange}
            onEmailFocus={handleEmailFocus}
            onPhoneConsentChange={handlePhoneConsentChange}
            onSubmit={handleSubmit}
            phoneConsent={phoneConsent}
            phoneConsentId={`${phoneConsentId}-desktop`}
            phoneError={errors.phone}
            phoneErrorId={`${phoneErrorId}-desktop`}
            phoneInputId={`${phoneInputId}-desktop`}
            phoneInputRef={phoneInputRef}
            phoneIsPressed={phoneIsPressed}
            phoneNumber={phoneNumber}
            showCellField={isExpanded}
            submitError={submitError}
          />
          <FormMobile
            email={email}
            emailConsent={emailConsent}
            emailConsentError={errors.emailConsent}
            emailConsentErrorId={`${emailConsentErrorId}-mobile`}
            emailConsentId={`${emailConsentId}-mobile`}
            emailError={errors.email}
            emailErrorId={`${emailErrorId}-mobile`}
            emailInputId={`${emailInputId}-mobile`}
            emailInputRef={emailInputRef}
            emailIsPressed={emailIsPressed}
            isSubmitting={isSubmitting}
            onCellNumberChange={handlePhoneChange}
            onCellNumberFocus={handlePhoneFocus}
            onCellNumberKeyDown={handlePhoneKeyDown}
            onEmailChange={handleEmailChange}
            onEmailConsentChange={handleEmailConsentChange}
            onEmailFocus={handleEmailFocus}
            onPhoneConsentChange={handlePhoneConsentChange}
            onSubmit={handleSubmit}
            phoneConsent={phoneConsent}
            phoneConsentId={`${phoneConsentId}-mobile`}
            phoneError={errors.phone}
            phoneErrorId={`${phoneErrorId}-mobile`}
            phoneInputId={`${phoneInputId}-mobile`}
            phoneInputRef={phoneInputRef}
            phoneIsPressed={phoneIsPressed}
            phoneNumber={phoneNumber}
            showCellField={isExpanded}
            submitError={submitError}
          />
        </>
      )}
    </ModalShell>
  )
}
